import React from "react";
import {
  Box,
  Typography,
  useTheme,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";

const LargeProgressCircle = ({ completed, inProgress, total, loading }) => {
  if (loading) {
    return <CircularProgress size={28} thickness={4} sx={{ ml: 1 }} />;
  }
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const completedPercentage = total > 0 ? (completed / total) * 100 : 0;
  const inProgressPercentage = total > 0 ? (inProgress / total) * 100 : 0;
  const completedDashArray = (completedPercentage / 100) * circumference;
  const inProgressDashArray = (inProgressPercentage / 100) * circumference;

  const tooltipContent = (
    <Box sx={{ p: 1, minWidth: 150 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
      >
        Assessment Status
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Completed
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#4caf50" }}>
          {completed}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          In Progress
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: "#ff9800" }}>
          {inProgress}
        </Typography>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Total
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "text.primary" }}
        >
          {total}
        </Typography>
      </Box>
    </Box>
  );

  if (total === 0) return null;
  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: 3,
            "& .MuiTooltip-arrow": {
              color: "background.paper",
              "&::before": {
                border: "1px solid",
                borderColor: "divider",
              },
            },
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
        <svg width="36" height="36" style={{ transform: "rotate(-90deg)" }}>
          {/* Background circle */}
          <circle
            cx="18"
            cy="18"
            r={radius}
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="3"
          />
          {/* Completed progress */}
          <circle
            cx="18"
            cy="18"
            r={radius}
            fill="none"
            stroke="#4caf50"
            strokeWidth="3"
            strokeDasharray={`${completedDashArray} ${circumference}`}
            strokeLinecap="round"
          />
          {/* In Progress (starts after completed) */}
          <circle
            cx="18"
            cy="18"
            r={radius}
            fill="none"
            stroke="#ff9800"
            strokeWidth="3"
            strokeDasharray={`${inProgressDashArray} ${circumference}`}
            strokeDashoffset={-completedDashArray}
            strokeLinecap="round"
          />
        </svg>
      </Box>
    </Tooltip>
  );
};

const WorkloadAssessmentHeader = ({ wafrMetric, wafrMetricLoading, wafrMetricError }) => {
  const theme = useTheme();

  // Always show 0s if error
  let wafr = {};
  if (wafrMetricError) {
    wafr = {
      totalWorkloads: 0,
      workloadsWithHighRisks: 0,
      totalHighRisks: 0,
      workloadsWithMediumRisks: 0,
      totalMediumRisks: 0,
      totalWorkloadsInProgress: 0,
      totalWorkloadsCompleted: 0,
    };
  } else {
    wafr = wafrMetric?.data || {};
  }

  const totalAssessments = wafr.totalWorkloads ?? 0;
  const assessmentsHighRisk = wafr.workloadsWithHighRisks ?? 0;
  const totalHighRiskIssues = wafr.totalHighRisks ?? 0;
  const assessmentsMediumRisk = wafr.workloadsWithMediumRisks ?? 0;
  const totalMediumRiskIssues = wafr.totalMediumRisks ?? 0;
  const assessmentsInProgress = wafr.totalWorkloadsInProgress ?? 0;
  const assessmentsCompleted = wafr.totalWorkloadsCompleted ?? 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        mb: 3,
        mt: 1,
        width: "100%",
      }}
    >
      {/* Total assessments */}
      <Box
        sx={{
          flex: "1 1 220px",
          minWidth: 180,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          boxShadow: 0,
          height: 100,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "text.primary",
            fontWeight: 600,
            mb: 0.5,
            textAlign: "left",
            fontFamily: "inherit",
          }}
        >
          Total Assessments
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
            mt: 0.5,
          }}
        >
          {wafrMetricLoading ? (
            <CircularProgress size={32} thickness={4} />
          ) : (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                textAlign: "left",
                lineHeight: 1,
                fontFamily: "inherit",
              }}
            >
              {totalAssessments}
            </Typography>
          )}
          <LargeProgressCircle
            completed={assessmentsCompleted}
            inProgress={assessmentsInProgress}
            total={totalAssessments}
            loading={wafrMetricLoading}
          />
        </Box>
      </Box>
      {/* High risk group */}
      <Box
        sx={{
          flex: "2 1 400px",
          minWidth: 320,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2.5,
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: "flex-start",
          boxShadow: 0,
          height: 100,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              mb: 0.5,
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            Assessments with high risk issues
          </Typography>
          {wafrMetricLoading ? (
            <CircularProgress size={28} thickness={4} />
          ) : (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                textAlign: "left",
                lineHeight: 1,
                fontFamily: "inherit",
              }}
            >
              {assessmentsHighRisk}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: "1px",
            background: theme.palette.divider,
            mx: 3,
            my: 0.5,
            alignSelf: "stretch",
          }}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              mb: 0.5,
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            Total high risk issues
          </Typography>
          {wafrMetricLoading ? (
            <CircularProgress size={28} thickness={4} />
          ) : (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                textAlign: "left",
                lineHeight: 1,
                fontFamily: "inherit",
              }}
            >
              {totalHighRiskIssues}
            </Typography>
          )}
        </Box>
      </Box>
      {/* Medium risk group */}
      <Box
        sx={{
          flex: "2 1 400px",
          minWidth: 320,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2.5,
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: "flex-start",
          boxShadow: 0,
          height: 100,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              mb: 0.5,
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            Assessments with medium risk issues
          </Typography>
          {wafrMetricLoading ? (
            <CircularProgress size={28} thickness={4} />
          ) : (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                textAlign: "left",
                lineHeight: 1,
                fontFamily: "inherit",
              }}
            >
              {assessmentsMediumRisk}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: "1px",
            background: theme.palette.divider,
            mx: 3,
            my: 0.5,
            alignSelf: "stretch",
          }}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              mb: 0.5,
              textAlign: "left",
              fontFamily: "inherit",
            }}
          >
            Total medium risk issues
          </Typography>
          {wafrMetricLoading ? (
            <CircularProgress size={28} thickness={4} />
          ) : (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                textAlign: "left",
                lineHeight: 1,
                fontFamily: "inherit",
              }}
            >
              {totalMediumRiskIssues}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WorkloadAssessmentHeader;
